import { useEffect, useState, useRef } from 'react';
import { paymentSlipsAPI, notificationsAPI } from '../../services/api';

export default function AdmissionReceipts() {
  const [status, setStatus] = useState('missing');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [selected, setSelected] = useState({}); // { [memberId]: member }
  const [notifyOpen, setNotifyOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => { setQuery(search); setPage(1); }, 300);
    return () => clearTimeout(timer);
  }, [search]);
  useEffect(() => {
    let active = true;
    setLoading(true); setError('');
    paymentSlipsAPI.admissionReceipts(page, query, status)
      .then((result) => { if (active) setData(result); })
      .catch((err) => { if (active) setError(err.response?.data?.error || 'Failed to load admission receipts.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [page, query, status, refresh]);

  const members = data?.members || [];
  const pageAllSelected = members.length > 0 && members.every((m) => selected[m.id]);
  const selectedList = Object.values(selected);

  function toggleOne(member) {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[member.id]) delete next[member.id]; else next[member.id] = member;
      return next;
    });
  }
  function togglePage() {
    setSelected((prev) => {
      const next = { ...prev };
      if (pageAllSelected) { members.forEach((m) => delete next[m.id]); }
      else { members.forEach((m) => { next[m.id] = m; }); }
      return next;
    });
  }

  return <section className="space-y-4">
    <div>
      <h2 className="text-xl font-bold text-slate-800">2026 Admission Receipts</h2>
      <p className="mt-1 text-sm text-slate-600">Members admitted from 1 January to 31 December 2026. Receipt upload is tracked separately from payment approval.</p>
    </div>
    <div className="grid gap-3 sm:grid-cols-3" role="group" aria-label="Receipt upload filters">
      {[['all', 'All admitted in 2026'], ['uploaded', 'Receipt uploaded'], ['missing', 'No receipt uploaded']].map(([key, label]) => <button key={key} type="button" aria-pressed={status === key} onClick={() => { setStatus(key); setPage(1); }}
        className={`rounded-xl border p-4 text-left ${status === key ? 'border-orange-500 bg-orange-50 ring-1 ring-orange-500' : 'border-slate-200 bg-white hover:bg-slate-50'}`}>
        <span className="block text-sm text-slate-600">{label}</span>
        <span className="mt-1 block text-2xl font-bold text-slate-900">{data ? Number(data.counts[key]).toLocaleString() : '—'}</span>
      </button>)}
    </div>
    <div className="flex flex-wrap items-center gap-3">
      <input aria-label="Search admission receipts" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, roll number, phone or email…" className="min-w-0 flex-1 rounded-xl border border-slate-200 px-3 py-2.5 text-sm" />
      <button type="button" onClick={() => setRefresh((value) => value + 1)} disabled={loading} className="rounded-xl border border-slate-200 bg-white px-4 text-sm disabled:opacity-50">Refresh</button>
      <button type="button" disabled={selectedList.length === 0} onClick={() => setNotifyOpen(true)}
        className="rounded-xl bg-orange-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-orange-500 disabled:opacity-40">
        📣 Notify {selectedList.length > 0 ? `(${selectedList.length})` : ''}
      </button>
    </div>
    {loading ? <p role="status" className="py-8 text-center text-slate-500">Loading members…</p> : error ? <p role="alert" className="rounded-xl bg-red-50 p-4 text-red-700">{error}</p> : <>
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">{data?.total || 0} members · {status === 'missing' ? 'No receipt uploaded' : status === 'uploaded' ? 'Receipt uploaded' : 'All admitted in 2026'}</p>
        <label className="flex items-center gap-1.5 text-xs font-medium text-slate-600 sm:hidden">
          <input type="checkbox" checked={pageAllSelected} onChange={togglePage} /> Select all on page
        </label>
      </div>

      {/* Desktop / wide screens: table */}
      <div className="hidden overflow-x-auto rounded-xl border border-slate-200 bg-white sm:block">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 text-slate-600"><tr>
            <th className="whitespace-nowrap px-4 py-3"><input type="checkbox" checked={pageAllSelected} onChange={togglePage} aria-label="Select all on this page" /></th>
            {['Member', 'Roll / Petition No.', 'Admission Date', 'Phone', 'Email', 'Receipt'].map((label) => <th key={label} className="whitespace-nowrap px-4 py-3 font-semibold">{label}</th>)}
          </tr></thead>
          <tbody>{members.map((member) => <tr key={member.id} className={`border-t border-slate-100 ${selected[member.id] ? 'bg-orange-50/50' : ''}`}>
            <td className="px-4 py-3"><input type="checkbox" checked={!!selected[member.id]} onChange={() => toggleOne(member)} aria-label={`Select ${member.full_name}`} /></td>
            <td className="min-w-48 px-4 py-3 font-medium text-slate-800">{member.full_name}</td>
            <td className="px-4 py-3">{member.roll_no || member.petition_no || '—'}</td>
            <td className="whitespace-nowrap px-4 py-3">{member.admission_date?.slice(0, 10)}</td>
            <td className="whitespace-nowrap px-4 py-3">{member.phone || '—'}</td>
            <td className="px-4 py-3">{member.email || '—'}</td>
            <td className="whitespace-nowrap px-4 py-3"><span className={Number(member.has_receipt) ? 'text-emerald-700' : 'text-amber-700'}>{Number(member.has_receipt) ? 'Uploaded' : 'Not uploaded'}</span></td>
          </tr>)}</tbody>
        </table>
        {!data?.members?.length && <p className="p-8 text-center text-slate-500">No members match this filter.</p>}
      </div>

      {/* Mobile: cards instead of a cramped table */}
      <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white sm:hidden">
        {members.map((member) => (
          <label key={member.id} className={`flex items-start gap-3 p-3 ${selected[member.id] ? 'bg-orange-50/50' : ''}`}>
            <input type="checkbox" checked={!!selected[member.id]} onChange={() => toggleOne(member)}
              aria-label={`Select ${member.full_name}`} className="mt-1 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <p className="min-w-0 truncate font-medium text-slate-800">{member.full_name}</p>
                <span className={`shrink-0 text-xs font-semibold ${Number(member.has_receipt) ? 'text-emerald-700' : 'text-amber-700'}`}>{Number(member.has_receipt) ? 'Uploaded' : 'Not uploaded'}</span>
              </div>
              <p className="mt-0.5 text-xs text-slate-500">{member.roll_no || member.petition_no || '—'} · {member.admission_date?.slice(0, 10)}</p>
              <p className="mt-0.5 truncate text-xs text-slate-500">{member.phone || '—'}{member.email && <> · {member.email}</>}</p>
            </div>
          </label>
        ))}
        {!data?.members?.length && <p className="p-8 text-center text-slate-500">No members match this filter.</p>}
      </div>

      <div className="flex items-center justify-between gap-2 text-sm">
        <span>Page {data?.page || 1} of {data?.total_pages || 1}</span>
        <div className="flex gap-2">
          <button type="button" disabled={!data || data.page <= 1} onClick={() => setPage(data.page - 1)} className="rounded-lg border px-3 py-2 disabled:opacity-40">Previous</button>
          <button type="button" disabled={!data || data.page >= data.total_pages} onClick={() => setPage(data.page + 1)} className="rounded-lg border px-3 py-2 disabled:opacity-40">Next</button>
        </div>
      </div>
    </>}

    {notifyOpen && (
      <NotifyModal
        recipients={selectedList}
        onClose={() => setNotifyOpen(false)}
        onSent={() => { setSelected({}); setNotifyOpen(false); }}
      />
    )}
  </section>;
}

const DEFAULT_MESSAGE = 'Habari, tafadhali ingia kwenye akaunti yako ya AWTF na uambatanishe uthibitisho wa malipo (proof of payment) uliyoyafanya. Ingia hapa: https://awtf.or.tz — Asante.';

function NotifyModal({ recipients, onClose, onSent }) {
  const [channel, setChannel] = useState('sms');
  const [subject, setSubject] = useState('AWTF: Weka uthibitisho wa malipo');
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const noPhoneCount = channel !== 'email' ? recipients.filter((m) => !m.phone).length : 0;
  const noEmailCount = channel !== 'sms' ? recipients.filter((m) => !m.email).length : 0;

  async function handleSend() {
    if (!message.trim()) { setError('Message text is required.'); return; }
    setSending(true); setError('');
    const ids = recipients.map((m) => m.id);
    try {
      // Email is always paced slowly in the background (notifications:process) so
      // Gmail/Yahoo don't flag a burst as spam — SMS-only stays instant.
      if (channel !== 'sms') {
        const resp = await notificationsAPI.queue(ids, channel, message.trim(), subject.trim());
        setJob({ id: resp.job_id, total: resp.total, processed: 0, sent: 0, failed: 0, status: 'pending' });
        pollRef.current = setInterval(async () => {
          try {
            const r = await notificationsAPI.jobStatus(resp.job_id);
            setJob(r.job);
            if (r.job.status === 'completed' || r.job.status === 'cancelled') clearInterval(pollRef.current);
          } catch { /* keep polling on transient errors */ }
        }, 3000);
      } else {
        const resp = await notificationsAPI.send(ids, channel, message.trim(), subject.trim());
        setResult(resp);
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to send notifications.');
      setSending(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/40 px-4 py-6">
      <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-slate-800">Notify {recipients.length} member{recipients.length === 1 ? '' : 's'}</h3>
          <button type="button" onClick={onClose} className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700">✕</button>
        </div>

        {job ? (
          <div className="mt-4 space-y-3">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-center justify-between text-sm">
                <span className="font-semibold text-slate-700">{job.status === 'completed' ? 'Done' : job.status === 'cancelled' ? 'Cancelled' : 'Sending…'}</span>
                <span className="text-slate-500">{job.processed.toLocaleString()} / {job.total.toLocaleString()}</span>
              </div>
              <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-200">
                <div className="h-full bg-orange-500 transition-all" style={{ width: `${job.total ? (job.processed / job.total) * 100 : 0}%` }} />
              </div>
              <p className="mt-2 text-xs text-slate-500">✓ {job.sent.toLocaleString()} sent · ✕ {job.failed.toLocaleString()} failed</p>
              {job.status === 'running' && <p className="mt-1 text-[11px] text-slate-400">This runs in the background (paced to avoid spam filters) — you can close this and check back later.</p>}
            </div>
            <div className="flex justify-end gap-2">
              {job.status !== 'completed' && job.status !== 'cancelled' && (
                <button type="button" onClick={async () => {
                  if (!window.confirm('Stop this notification job? Members already sent to have been sent to — this only stops the rest.')) return;
                  try {
                    await notificationsAPI.cancelJob(job.id);
                    const r = await notificationsAPI.jobStatus(job.id);
                    setJob(r.job);
                  } catch (e) { setError(e.response?.data?.error || 'Failed to cancel.'); }
                }} className="rounded-xl border border-red-300 px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50">
                  Cancel
                </button>
              )}
              <button type="button" onClick={onSent} className="rounded-xl bg-slate-800 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-900">
                {job.status === 'completed' || job.status === 'cancelled' ? 'Done' : 'Close (keeps sending)'}
              </button>
            </div>
          </div>
        ) : result ? (
          <div className="mt-4 space-y-3">
            <p className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{result.message}</p>
            <div className="max-h-56 overflow-y-auto rounded-xl border border-slate-200">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 text-slate-500"><tr><th className="px-3 py-2">Member</th>{channel !== 'email' && <th className="px-3 py-2">SMS</th>}{channel !== 'sms' && <th className="px-3 py-2">Email</th>}</tr></thead>
                <tbody>{result.results.map((r) => (
                  <tr key={r.member_id} className="border-t border-slate-100">
                    <td className="px-3 py-2 text-slate-700">{r.full_name}</td>
                    {channel !== 'email' && <td className={`px-3 py-2 ${r.sms === 'sent' ? 'text-emerald-600' : 'text-red-600'}`}>{r.sms || '—'}</td>}
                    {channel !== 'sms' && <td className={`px-3 py-2 ${r.email === 'sent' ? 'text-emerald-600' : 'text-red-600'}`}>{r.email || '—'}</td>}
                  </tr>
                ))}</tbody>
              </table>
            </div>
            <div className="flex justify-end">
              <button type="button" onClick={onSent} className="rounded-xl bg-slate-800 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-900">Done</button>
            </div>
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            {error && <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Channel</label>
              <div className="flex gap-2">
                {[['sms', 'SMS'], ['email', 'Email'], ['both', 'SMS + Email']].map(([key, label]) => (
                  <button key={key} type="button" onClick={() => setChannel(key)}
                    className={`rounded-xl border px-3 py-2 text-xs font-semibold ${channel === key ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                    {label}
                  </button>
                ))}
              </div>
              {(noPhoneCount > 0 || noEmailCount > 0) && (
                <p className="mt-1.5 text-[11px] text-amber-600">
                  {noPhoneCount > 0 && `${noPhoneCount} recipient(s) have no phone on file. `}
                  {noEmailCount > 0 && `${noEmailCount} recipient(s) have no email on file.`}
                </p>
              )}
              {channel !== 'sms' && (
                <p className="mt-1.5 text-[11px] text-slate-500">Email is sent gradually in the background (~1,000/hour) to avoid Gmail/Yahoo flagging it as spam.</p>
              )}
            </div>

            {channel !== 'sms' && (
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Email subject</label>
                <input value={subject} onChange={(e) => setSubject(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
              </div>
            )}

            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Message</label>
              <textarea rows={5} value={message} onChange={(e) => setMessage(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={onClose} disabled={sending}
                className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-60">
                Cancel
              </button>
              <button type="button" onClick={handleSend} disabled={sending}
                className="rounded-xl bg-orange-600 hover:bg-orange-500 disabled:opacity-60 text-white px-4 py-2 text-sm font-semibold">
                {sending ? 'Sending…' : `Send to ${recipients.length}`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
